import { useState, useEffect, useCallback } from 'react';
import api, { crmAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Users, Link2, RefreshCw, CheckCircle, AlertTriangle, XCircle, UserPlus, ChevronDown, ChevronRight, ShieldAlert } from 'lucide-react';

const CONF_COLORS = {
  HIGH: 'bg-emerald-100 text-emerald-800',
  MED: 'bg-blue-100 text-blue-800',
  LOW: 'bg-amber-100 text-amber-800',
  UNMATCHED: 'bg-red-100 text-red-800',
};

export default function CRMMatchingPage() {
  const [status, setStatus] = useState(null);
  const [unmatchedVendors, setUnmatchedVendors] = useState([]);
  const [unmatchedCustomers, setUnmatchedCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [matching, setMatching] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [ownership, setOwnership] = useState(null);
  const [ownershipLoading, setOwnershipLoading] = useState(false);
  const [ownershipError, setOwnershipError] = useState(null);
  const [conflictsOpen, setConflictsOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, vendorsRes, customersRes] = await Promise.all([
        crmAPI.getMatchingStatus(),
        crmAPI.getUnmatchedVendors(50),
        crmAPI.getUnmatchedCustomers(50),
      ]);
      setStatus(statusRes.data);
      setUnmatchedVendors(vendorsRes.data.vendors || []);
      setUnmatchedCustomers(customersRes.data.customers || []);
    } catch (e) {
      toast.error('Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchOwnershipCheck = useCallback(async () => {
    setOwnershipLoading(true);
    setOwnershipError(null);
    try {
      const [outOfScopeRes, unownedRes, stuckRes, runRes] = await Promise.all([
        crmAPI.getOwnershipOutOfScopeRequests(),
        crmAPI.getOwnershipUnownedVendors(),
        crmAPI.getOwnershipStuckApprovers(),
        crmAPI.getOwnershipLatestMatchingRun(),
      ]);
      setOwnership({
        outOfScope: outOfScopeRes.data.rows || [],
        unowned: unownedRes.data.rows || [],
        stuck: stuckRes.data.rows || [],
        run: runRes.data,
      });
    } catch (e) {
      const msg = e?.response?.status === 403
        ? "You don't have access to the ownership check (needs CRM Integration / RECONCILE view rights)."
        : 'Failed to load the ownership check.';
      setOwnershipError(msg);
      toast.error(msg);
    } finally {
      setOwnershipLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'ownership_check' && !ownership && !ownershipError) fetchOwnershipCheck();
  }, [activeTab, ownership, ownershipError, fetchOwnershipCheck]);

  const handleSyncUsers = async () => {
    setSyncing(true);
    try {
      const r = await crmAPI.syncUsers();
      toast.success(`Synced ${r.data.crm_users_total} CRM users. ${r.data.users_created} new users created.`);
      fetchData();
    } catch (e) {
      toast.error('Failed to sync CRM users');
    } finally {
      setSyncing(false);
    }
  };

  const handleRunMatching = async () => {
    setMatching(true);
    try {
      const r = await crmAPI.runMatching();
      if (r.data.status === 'aborted_short_fetch') {
        toast.error('Matching stopped: the CRM fetch looked incomplete. Nothing was changed.');
      } else {
        const v = r.data.vendors?.matches || {};
        const c = r.data.customers?.matches || {};
        toast.success(`Matching complete. Vendors: ${(v.HIGH||0)+(v.MED||0)+(v.LOW||0)} matched. Customers: ${(c.HIGH||0)+(c.MED||0)+(c.LOW||0)} matched.`);
      }
      fetchData();
    } catch (e) {
      toast.error('Failed to run matching');
    } finally {
      setMatching(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

  const v = status?.vendors || {};
  const c = status?.customers || {};
  const u = status?.users || {};

  return (
    <div className="space-y-6" data-testid="crm-matching-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Link2 className="h-6 w-6 text-primary" />CRM Integration
          </h1>
          <p className="text-muted-foreground text-sm">Zoho CRM user sync, vendor/customer matching & ownership</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSyncUsers} disabled={syncing} className="rounded-sm" data-testid="sync-users-btn">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
            Sync CRM Users
          </Button>
          <Button variant="outline" onClick={async () => {
            try {
              const r = await api.post('/users/sync-hierarchy-from-crm');
              const applied = r.data.applied || {};
              toast.success(r.data.message ? `${r.data.message} — ${applied.updated || 0} reporting line(s) updated (${applied.checked || 0} checked)` : 'Hierarchy synced');
              fetchData();
            } catch(e) { toast.error(e?.response?.data?.detail || 'Failed to sync hierarchy'); }
          }} className="rounded-sm" data-testid="sync-hierarchy-btn">
            Sync Hierarchy
          </Button>
          <Button onClick={handleRunMatching} disabled={matching} className="rounded-sm" data-testid="run-matching-btn">
            {matching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
            Run Matching
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="rounded-sm" data-testid="users-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1"><Users className="h-4 w-4" /><span className="text-xs font-medium uppercase">CRM Users</span></div>
            <p className="text-2xl font-bold font-mono">{u.crm_active || 0}</p>
            <p className="text-xs text-muted-foreground">{u.auto_created || 0} auto-created in TradeIQ</p>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-emerald-200" data-testid="vendor-matched-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1"><CheckCircle className="h-4 w-4" /><span className="text-xs font-medium uppercase">Vendors Matched</span></div>
            <p className="text-2xl font-bold font-mono text-emerald-600">{v.matched || 0}</p>
            <p className="text-xs text-muted-foreground">of {v.total || 0} total</p>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-emerald-200" data-testid="customer-matched-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1"><CheckCircle className="h-4 w-4" /><span className="text-xs font-medium uppercase">Customers Matched</span></div>
            <p className="text-2xl font-bold font-mono text-emerald-600">{c.matched || 0}</p>
            <p className="text-xs text-muted-foreground">of {c.total || 0} total</p>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-red-200" data-testid="unmatched-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-600 mb-1"><XCircle className="h-4 w-4" /><span className="text-xs font-medium uppercase">Unmatched</span></div>
            <p className="text-2xl font-bold font-mono text-red-600">{(v.UNMATCHED || 0) + (c.UNMATCHED || 0)}</p>
            <p className="text-xs text-muted-foreground">{v.UNMATCHED || 0} vendors, {c.UNMATCHED || 0} customers</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {['overview', 'unmatched_vendors', 'unmatched_customers', 'ownership_check'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            data-testid={`tab-${tab}`}>
            {tab === 'overview' ? 'Matching Overview' : tab === 'unmatched_vendors' ? `Unmatched Vendors (${v.UNMATCHED || 0})` : tab === 'unmatched_customers' ? `Unmatched Customers (${c.UNMATCHED || 0})` : 'Ownership check'}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Vendor Confidence Breakdown */}
          <Card className="rounded-sm">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold mb-3">Vendor Match Quality</h3>
              <div className="space-y-2">
                {[
                  { label: 'HIGH (Email)', count: v.HIGH || 0, color: 'bg-emerald-500' },
                  { label: 'MED (Phone)', count: v.MED || 0, color: 'bg-blue-500' },
                  { label: 'LOW (Name)', count: v.LOW || 0, color: 'bg-amber-500' },
                  { label: 'Unmatched', count: v.UNMATCHED || 0, color: 'bg-red-500' },
                ].map(item => {
                  const pct = v.total > 0 ? (item.count / v.total * 100) : 0;
                  return (
                    <div key={item.label} className="flex items-center gap-3">
                      <span className="text-xs w-24 text-muted-foreground">{item.label}</span>
                      <div className="flex-1 h-4 bg-muted rounded-sm overflow-hidden">
                        <div className={`h-full ${item.color} rounded-sm`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-mono w-16 text-right">{item.count} ({pct.toFixed(0)}%)</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          {/* Customer Confidence Breakdown */}
          <Card className="rounded-sm">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold mb-3">Customer Match Quality</h3>
              <div className="space-y-2">
                {[
                  { label: 'HIGH (Email)', count: c.HIGH || 0, color: 'bg-emerald-500' },
                  { label: 'MED (Phone)', count: c.MED || 0, color: 'bg-blue-500' },
                  { label: 'LOW (Name)', count: c.LOW || 0, color: 'bg-amber-500' },
                  { label: 'Unmatched', count: c.UNMATCHED || 0, color: 'bg-red-500' },
                ].map(item => {
                  const pct = c.total > 0 ? (item.count / c.total * 100) : 0;
                  return (
                    <div key={item.label} className="flex items-center gap-3">
                      <span className="text-xs w-24 text-muted-foreground">{item.label}</span>
                      <div className="flex-1 h-4 bg-muted rounded-sm overflow-hidden">
                        <div className={`h-full ${item.color} rounded-sm`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-mono w-16 text-right">{item.count} ({pct.toFixed(0)}%)</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'unmatched_vendors' && (
        <Card className="rounded-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead className="text-xs font-medium">Vendor Name</TableHead>
                <TableHead className="text-xs font-medium">Email</TableHead>
                <TableHead className="text-xs font-medium">Phone</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {unmatchedVendors.length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center py-8"><CheckCircle className="h-10 w-10 mx-auto text-emerald-400 mb-2" /><p className="text-muted-foreground">All vendors matched</p></TableCell></TableRow>
                ) : unmatchedVendors.map((v, i) => (
                  <TableRow key={i} data-testid={`unmatched-vendor-${i}`}>
                    <TableCell className="text-sm font-medium">{v.vendor_name || v.contact_name || '—'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{v.email || '—'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{v.phone || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {activeTab === 'unmatched_customers' && (
        <Card className="rounded-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead className="text-xs font-medium">Customer Name</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {unmatchedCustomers.length === 0 ? (
                  <TableRow><TableCell className="text-center py-8"><CheckCircle className="h-10 w-10 mx-auto text-emerald-400 mb-2" /><p className="text-muted-foreground">All customers matched</p></TableCell></TableRow>
                ) : unmatchedCustomers.map((c, i) => (
                  <TableRow key={i} data-testid={`unmatched-customer-${i}`}>
                    <TableCell className="text-sm font-medium">{c.customer_name || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      {activeTab === 'ownership_check' && (
        <div className="space-y-6" data-testid="ownership-check-tab">
          <p className="text-sm text-muted-foreground">Read-only. Nothing on this page changes data — it's the same checks the production reports ran, live.</p>
          {ownershipError ? (
            <div className="flex flex-col items-center gap-3 py-16" data-testid="ownership-check-error">
              <XCircle className="h-8 w-8 text-red-400" />
              <p className="text-sm text-muted-foreground">{ownershipError}</p>
              <Button variant="outline" size="sm" onClick={fetchOwnershipCheck} data-testid="ownership-check-retry-btn">
                <RefreshCw className="h-4 w-4 mr-1" /> Retry
              </Button>
            </div>
          ) : ownershipLoading || !ownership ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              <Card className="rounded-sm">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold mb-1">Payment requests now out of scope for their uploader</h3>
                  <p className="text-xs text-muted-foreground mb-3">Last 30 days — the PO vendor's ownership or the uploader's reporting line changed since they submitted.</p>
                  <Table>
                    <TableHeader><TableRow className="bg-muted/50">
                      <TableHead className="text-xs font-medium">Vendor</TableHead>
                      <TableHead className="text-xs font-medium">Uploader</TableHead>
                      <TableHead className="text-xs font-medium text-right">Count</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {ownership.outOfScope.length === 0 ? (
                        <TableRow><TableCell colSpan={3} className="text-center py-6"><CheckCircle className="h-8 w-8 mx-auto text-emerald-400 mb-1" /><p className="text-muted-foreground text-sm">None found</p></TableCell></TableRow>
                      ) : ownership.outOfScope.map((r, i) => (
                        <TableRow key={i} data-testid={`out-of-scope-row-${i}`}>
                          <TableCell className="text-sm font-medium">{r.vendor_name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{r.uploader_name} ({r.uploader_email})</TableCell>
                          <TableCell className="text-sm font-mono text-right">{r.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card className="rounded-sm">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold mb-1">Vendors with no Trade IQ owner</h3>
                  <p className="text-xs text-muted-foreground mb-3">Had a PO or bill in the last 90 days, no assigned Trade IQ user.</p>
                  <Table>
                    <TableHeader><TableRow className="bg-muted/50">
                      <TableHead className="text-xs font-medium">Vendor</TableHead>
                      <TableHead className="text-xs font-medium text-right">PO + Bill count</TableHead>
                      <TableHead className="text-xs font-medium">CRM match confidence</TableHead>
                      <TableHead className="text-xs font-medium">CRM owner (not a Trade IQ user)</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {ownership.unowned.length === 0 ? (
                        <TableRow><TableCell colSpan={4} className="text-center py-6"><CheckCircle className="h-8 w-8 mx-auto text-emerald-400 mb-1" /><p className="text-muted-foreground text-sm">None found</p></TableCell></TableRow>
                      ) : ownership.unowned.map((r, i) => (
                        <TableRow key={i} data-testid={`unowned-vendor-row-${i}`}>
                          <TableCell className="text-sm font-medium">{r.vendor_name}</TableCell>
                          <TableCell className="text-sm font-mono text-right">{r.po_bill_count}</TableCell>
                          <TableCell className="text-sm"><Badge className={`${CONF_COLORS[r.crm_match_confidence] || 'bg-muted'} text-xs`}>{r.crm_match_confidence || '—'}</Badge></TableCell>
                          <TableCell className="text-sm text-muted-foreground">{r.crm_owner_email || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card className="rounded-sm">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-amber-600" />Requests waiting on an approver who can't approve</h3>
                  <p className="text-xs text-muted-foreground mb-3">Currently SUBMITTED, waiting on someone without Vendor Payment Approval rights.</p>
                  <Table>
                    <TableHeader><TableRow className="bg-muted/50">
                      <TableHead className="text-xs font-medium">PO</TableHead>
                      <TableHead className="text-xs font-medium">Vendor</TableHead>
                      <TableHead className="text-xs font-medium">Stuck approver</TableHead>
                      <TableHead className="text-xs font-medium">Submitted</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {ownership.stuck.length === 0 ? (
                        <TableRow><TableCell colSpan={4} className="text-center py-6"><CheckCircle className="h-8 w-8 mx-auto text-emerald-400 mb-1" /><p className="text-muted-foreground text-sm">None found</p></TableCell></TableRow>
                      ) : ownership.stuck.map((r, i) => (
                        <TableRow key={i} data-testid={`stuck-approver-row-${i}`}>
                          <TableCell className="text-sm font-medium">{r.po_number}</TableCell>
                          <TableCell className="text-sm">{r.vendor_name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{r.approver_name} ({r.approver_email})</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card className="rounded-sm">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold mb-3">Last matching run</h3>
                  {!ownership.run.found ? (
                    <p className="text-sm text-muted-foreground">No matching run recorded yet.</p>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-xs text-muted-foreground">{new Date(ownership.run.timestamp).toLocaleString()}</p>
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                        {[
                          { label: 'CRM vendors fetched', value: ownership.run.crm_total, testid: 'run-crm-total' },
                          { label: 'Trade IQ vendors matched', value: ownership.run.matched, testid: 'run-matched' },
                          { label: 'Trade IQ vendors unmatched', value: ownership.run.unmatched, testid: 'run-unmatched' },
                          { label: 'Owner not a Trade IQ user', value: ownership.run.owner_not_tradeiq_user, testid: 'run-owner-not-user' },
                          { label: 'Owners changed', value: ownership.run.owners_changed, testid: 'run-owners-changed' },
                          { label: 'Conflicts', value: ownership.run.conflicts_count, testid: 'run-conflicts-count' },
                        ].map(item => (
                          <div key={item.label} className="bg-muted/30 rounded-sm p-2">
                            <p className="text-xs text-muted-foreground">{item.label}</p>
                            <p className="text-lg font-bold font-mono" data-testid={item.testid}>{item.value}</p>
                          </div>
                        ))}
                      </div>
                      {ownership.run.conflicts_count > 0 && (
                        <div>
                          <button onClick={() => setConflictsOpen(!conflictsOpen)} className="flex items-center gap-1 text-sm text-primary" data-testid="toggle-conflicts-btn">
                            {conflictsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            {conflictsOpen ? 'Hide' : 'Show'} conflicts ({ownership.run.conflicts_count})
                          </button>
                          {conflictsOpen && (
                            <div className="mt-2 max-h-80 overflow-y-auto border border-border rounded-sm" data-testid="conflicts-list">
                              <Table>
                                <TableHeader><TableRow className="bg-muted/50">
                                  <TableHead className="text-xs font-medium">Context</TableHead>
                                  <TableHead className="text-xs font-medium">Resolution</TableHead>
                                </TableRow></TableHeader>
                                <TableBody>
                                  {(ownership.run.conflicts || []).map((c, i) => (
                                    <TableRow key={i}>
                                      <TableCell className="text-xs font-mono">{c.context}</TableCell>
                                      <TableCell className="text-xs text-muted-foreground">{c.resolution}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
}
